export const GET_APIS = `
  query {
    apis {
      uuid
      tenantId
      name
      description
      version
      status
      specContent
      tags
      classifications
      entityClass
      architectReview
      documents {
        uuid
        name
        status
      }
    }
  }
`;

export const GET_API = `
  query GetAPI($uuid: ID!) {
    api(uuid: $uuid) {
      uuid
      tenantId
      name
      description
      version
      status
      specContent
      tags
      architectReview
      developerGuides {
        uuid
        name
        description
        status
        sections {
          id
          sortOrder
          title
          instructions
          codeSamples { id language label code }
        }
      }
      documents {
        uuid
        name
        status
      }
    }
  }
`;

export const GET_LINKS = `
  query GetLinks {
    links {
      uuid
      tenantId
      name
      description
      entityClass
      status
    }
  }
`;

export const GET_DOCUMENTS = `
  query GetDocuments {
    documents {
      uuid
      tenantId
      name
      status
      description
      tags
      entityClass
      classifications
      content
      sourceFormat
      sourceFileName
      expirationDate
      ragIndexStatus
      parents {
        entityType
        entityId
        name
      }
    }
  }
`;

export const GET_CATEGORIES = `
  query {
    categories {
      uuid
      tenantId
      name
      description
      status
      tags
    }
  }
`;

export const GET_PRODUCT_CATALOG = `
  query GetProductCatalog {
    productCatalog {
      uuid
      name
      description
      status
      type
      tags
      categoryNames
      heroImageName
      subscribable
    }
  }
`;

export const GET_PRODUCTS = `
  query GetAllProducts {
    products {
      uuid
      tenantId
      type
      name
      description
      status
      classifications
      entityClass
      approvalNeeded
      productOwners
      subscribable
      tags
      apis { uuid name status }
      categories { uuid name status }
      documents { uuid name status }
      faqs { uuid name status }
      links { uuid name status }
      developerGuides { uuid name status }
      heroImage { uuid name }
      relatedProducts { uuid name status }
    }
  }
`;

export const GET_PRODUCT = `
  query GetProduct($uuid: ID!) {
    product(uuid: $uuid) {
      uuid
      tenantId
      type
      name
      description
      status
      classifications
      entityClass
      tags
      apis { uuid name status specContent }
      categories { uuid name status }
      documents { uuid name description status }
      faqs { uuid name description question answer status }
      links { uuid name description entityClass status }
      developerGuides {
        uuid
        name
        description
        status
        sections {
          id
          sortOrder
          title
          instructions
          codeSamples { id language label code }
        }
      }
      heroImage { uuid name }
      relatedProducts { uuid name status }
      subscribable
      gatewaySkuAttachments {
        uuid
        attachmentName
        gatewaySku {
          uuid
          gatewayProductIdentifier
          gatewayProductName
          gatewayCommonName
          gatewayId
          gatewayType
          envName
          skuStatus
        }
      }
    }
  }
`;

export const GET_GATEWAY_SKUS = `
  query GetGatewaySkus {
    gatewaySkus {
      uuid
      tenantId
      name
      status
      description
      createdBy
      createdOn
      gatewayProductIdentifier
      gatewayProductName
      gatewayCommonName
      gatewayId
      gatewayType
      envName
      skuStatus
      syncedOn
      updatedOn
    }
  }
`;

const GATEWAY_ROUTE_FIELDS = `
  uuid
  tenantId
  name
  status
  description
  createdBy
  createdOn
  tags
  basePath
  upstreamServer
  upstreamPath
  envName
  gatewayId
  gatewayAuthMechanism
  gatewayAuthNotes
  upstreamAuthMechanism
  upstreamAuthNotes
  purpose
  dataSensitivity
  notes
  sloTargets {
    metric
    sliDescription
    sloTarget
    measurementWindow
    notes
  }
  gatewaySku {
    uuid
    gatewayCommonName
    gatewayId
    envName
  }
  apis { uuid name status }
  products { uuid name status }
`;

export const GET_GATEWAY_ROUTES = `
  query GetGatewayRoutes {
    gatewayRoutes {
      ${GATEWAY_ROUTE_FIELDS}
    }
  }
`;

export const GET_GATEWAY_ROUTE = `
  query GetGatewayRoute($uuid: ID!) {
    gatewayRoute(uuid: $uuid) {
      ${GATEWAY_ROUTE_FIELDS}
    }
  }
`;

export const SEARCH_GATEWAY_ROUTES = `
  query SearchGatewayRoutes($query: String!) {
    searchGatewayRoutes(query: $query) {
      ${GATEWAY_ROUTE_FIELDS}
    }
  }
`;

export const POTENTIAL_DUPLICATE_GATEWAY_ROUTES = `
  query PotentialDuplicateGatewayRoutes(
    $envName: String!
    $gatewayId: String!
    $basePath: String!
    $excludeUuid: ID
  ) {
    potentialDuplicateGatewayRoutes(
      envName: $envName
      gatewayId: $gatewayId
      basePath: $basePath
      excludeUuid: $excludeUuid
    ) {
      uuid
      name
      envName
      gatewayId
      basePath
      upstreamServer
      upstreamPath
    }
  }
`;

export const GET_GATEWAY_SKU = `
  query GetGatewaySku($uuid: ID!) {
    gatewaySku(uuid: $uuid) {
      uuid
      tenantId
      name
      status
      description
      createdBy
      createdOn
      gatewayProductIdentifier
      gatewayProductName
      gatewayCommonName
      gatewayId
      gatewayType
      envName
      skuStatus
      syncedOn
      updatedOn
    }
  }
`;

export const GET_DEVELOPER_GUIDES = `
  query GetDeveloperGuides {
    developerGuides {
      uuid
      tenantId
      name
      description
      status
      sections {
        id
        sortOrder
        title
        instructions
        codeSamples { id language label code }
      }
      apis { uuid name status }
      products { uuid name status }
    }
  }
`;

export const GET_DEVELOPER_GUIDE = `
  query GetDeveloperGuide($uuid: ID!) {
    developerGuide(uuid: $uuid) {
      uuid
      tenantId
      name
      description
      status
      sections {
        id
        sortOrder
        title
        instructions
        codeSamples { id language label code }
      }
      apis { uuid name status version specContent }
      products { uuid name status }
    }
  }
`;

export const GET_FAQS = `
  query GetFAQs {
    faqs {
      uuid
      tenantId
      name
      status
      description
      question
      answer
    }
  }
`;

export const GET_FAQ = `
  query GetFAQ($uuid: ID!) {
    faq(uuid: $uuid) {
      uuid
      tenantId
      name
      status
      description
      question
      answer
    }
  }
`;

export const GET_APP_KYCS = `
  query GetAppKycs {
    appKycs {
      uuid
      tenantId
      name
      status
      description
      createdBy
      createdOn
      appKeyId
      providerGatewayType
      providerGatewayIdentifier
      appKeyStatus
      keyCreatedOn
      keyExpiresOn
      contactName
      contactEmail
      contactPhone
      contactCompanyName
      contactCompanyWebsite
      bmContactName
      bmContactEmail
      itSupportName
      itSupportEmail
    }
  }
`;

export const GET_APP_KYC = `
  query GetAppKyc($uuid: ID!) {
    appKyc(uuid: $uuid) {
      uuid
      tenantId
      name
      status
      description
      createdBy
      createdOn
      appKeyId
      providerGatewayType
      providerGatewayIdentifier
      appKeyStatus
      keyCreatedOn
      keyExpiresOn
      contactName
      contactEmail
      contactPhone
      contactCompanyName
      contactCompanyWebsite
      bmContactName
      bmContactEmail
      itSupportName
      itSupportEmail
    }
  }
`;

export const GET_CATEGORY = `
  query GetCategory($uuid: ID!) {
    category(uuid: $uuid) {
      uuid
      tenantId
      name
      description
      status
      tags
    }
  }
`;

export const GET_LINK = `
  query GetLink($uuid: ID!) {
    link(uuid: $uuid) {
      uuid
      tenantId
      name
      description
      entityClass
      status
    }
  }
`;

export const GET_DOCUMENT = `
  query GetDocument($uuid: ID!) {
    document(uuid: $uuid) {
      uuid
      tenantId
      name
      status
      description
      tags
      entityClass
      classifications
      content
      sourceFormat
      sourceFileName
      expirationDate
      ragIndexStatus
      parents {
        entityType
        entityId
        name
      }
    }
  }
`;

export const GET_MY_SUBSCRIPTIONS = `
  query GetMySubscriptions {
    mySubscriptions {
      uuid
      name
      description
      status
      createdOn
      products {
        productStatus
        product {
          uuid
          name
          description
        }
      }
    }
  }
`;

const SUPPORT_CASE_FIELDS = `
  uuid
  tenantId
  type
  name
  description
  createdBy
  createdOn
  status
  tags
  classifications
  entityClass
  caseStatus
  assigneeEmail
  assigneeType
  resolvedAt
  closedAt
  product { uuid name }
`;

export const GET_MY_SUPPORT_CASES = `
  query GetMySupportCases {
    mySupportCases { ${SUPPORT_CASE_FIELDS} }
  }
`;

export const GET_AGENT_SUPPORT_CASES = `
  query GetAgentSupportCases {
    agentSupportCases { ${SUPPORT_CASE_FIELDS} }
  }
`;

export const GET_SUPPORT_CASE = `
  query GetSupportCase($uuid: ID!) {
    supportCase(uuid: $uuid) {
      ${SUPPORT_CASE_FIELDS}
      events {
        uuid
        eventType
        actorEmail
        fromStatus
        toStatus
        message
        metadata
        createdOn
      }
      comments {
        uuid
        authorEmail
        body
        commentType
        createdOn
      }
    }
  }
`;

export const GET_TENANT_SUPPORT_CASE_SETTINGS = `
  query GetTenantSupportCaseSettings {
    tenantSupportCaseSettings {
      tenantId
      autoCloseResolvedDays
      updatedBy
      updatedOn
    }
  }
`;

export const GET_TENANT_ORGANIZATION_SETTINGS = `
  query GetTenantOrganizationSettings {
    tenantOrganizationSettings {
      tenantId
      companyName
      address
      website
      phone
      contactEmail
      updatedBy
      updatedOn
    }
  }
`;

export const GET_TENANT_MEMBERS = `
  query GetTenantMembers {
    tenantMembers {
      tenantId
      email
      displayName
      status
      roles
      createdBy
      createdOn
      updatedOn
    }
  }
`;

export const GET_TENANT_DATA_EXPORT = `
  query GetTenantDataExport {
    tenantDataExport {
      tenantId
      exportedAt
      exportedBy
      filename
      json
    }
  }
`;

export const GET_TENANT_CONTACT_INFO = `
  query GetTenantContactInfo {
    tenantContactInfo {
      companyName
      address
      website
      phone
      contactEmail
    }
  }
`;

export const GET_PLATFORM_TENANTS = `
  query GetPlatformTenants {
    platformTenants {
      tenantId
      displayName
      status
      createdBy
      createdOn
      updatedOn
    }
  }
`;

export const GET_PLATFORM_TENANT_INSIGHTS = `
  query GetPlatformTenantInsights {
    platformTenantInsights {
      totalTenants
      totalActiveTenants
      totalDisabledTenants
      tenants {
        tenantId
        displayName
        status
        totalUsers
        usersByRole {
          role
          count
        }
        productCount
        apiCount
        categoryCount
        faqCount
        linkCount
        documentCount
        supportCaseCount
      }
    }
  }
`;

export const GET_PLATFORM_TENANT_ORGANIZATION_SETTINGS = `
  query GetPlatformTenantOrganizationSettings($tenantId: String!) {
    platformTenantOrganizationSettings(tenantId: $tenantId) {
      tenantId
      companyName
      address
      website
      phone
      contactEmail
      updatedBy
      updatedOn
    }
  }
`;

export const GET_PLATFORM_TENANT_ADMINS = `
  query GetPlatformTenantAdmins($tenantId: String!) {
    platformTenantAdmins(tenantId: $tenantId) {
      tenantId
      email
      displayName
      status
      roles
      createdBy
      createdOn
      updatedOn
    }
  }
`;

export const GET_PLATFORM_AGENT_RUNNER_BINDINGS = `
  query GetPlatformAgentRunnerBindings($tenantId: String) {
    platformAgentRunnerBindings(tenantId: $tenantId) {
      uuid
      tenantId
      agentId
      basePath
      updatedBy
      updatedOn
    }
  }
`;

export const RESOLVED_AGENT_RUNNER_BASE = `
  query ResolvedAgentRunnerBase($agentId: String!) {
    resolvedAgentRunnerBase(agentId: $agentId)
  }
`;

export const GET_SUBSCRIPTION_REQUESTS = `
  query GetSubscriptionRequests {
    subscriptionRequests {
      uuid
      name
      description
      status
      createdOn
      subscriberEmail
      products {
        productStatus
        product {
          uuid
          name
        }
      }
    }
  }
`;

const OPERATIONAL_ERROR_FIELDS = `
  uuid
  tenantId
  name
  description
  status
  tags
  code
  httpStatus
  category
  message
  remediation
  errorStrings
  products { uuid name }
  apis { uuid name }
`;

export const GET_OPERATIONAL_ERRORS = `
  query GetOperationalErrors {
    operationalErrors {
      ${OPERATIONAL_ERROR_FIELDS}
    }
  }
`;

export const SEARCH_OPERATIONAL_ERRORS = `
  query SearchOperationalErrors($query: String!) {
    searchOperationalErrors(query: $query) {
      ${OPERATIONAL_ERROR_FIELDS}
    }
  }
`;

export const GET_OPERATIONAL_ERROR = `
  query GetOperationalError($uuid: ID!) {
    operationalError(uuid: $uuid) {
      ${OPERATIONAL_ERROR_FIELDS}
    }
  }
`;

const KAFKA_EVENT_TOPIC_FIELDS = `
  uuid tenantId name description status tags classifications entityClass createdBy createdOn
  environment purpose schemaFormat schemaContent schemaVersion schemaSubject schemaRegistryUrl
  compatibilityLevel keySchemaFormat keySchemaContent partitionCount replicationFactor
  retentionMs samplePayload dataSensitivity notes
  products { uuid name }
  apis { uuid name }
`;

export const GET_KAFKA_EVENT_TOPICS = `
  query GetKafkaEventTopics {
    kafkaEventTopics {
      ${KAFKA_EVENT_TOPIC_FIELDS}
    }
  }
`;

export const SEARCH_KAFKA_EVENT_TOPICS = `
  query SearchKafkaEventTopics($query: String!) {
    searchKafkaEventTopics(query: $query) {
      ${KAFKA_EVENT_TOPIC_FIELDS}
    }
  }
`;

export const GET_KAFKA_EVENT_TOPIC = `
  query GetKafkaEventTopic($uuid: ID!) {
    kafkaEventTopic(uuid: $uuid) {
      ${KAFKA_EVENT_TOPIC_FIELDS}
    }
  }
`;

const API_INTAKE_FIELDS = `
  uuid tenantId name description createdBy createdOn status tags classifications entityClass
  requirementText relatedDocsMarkdown evaluation coverage coveragePercent recommendedChangePath
  recommendedApi { uuid name version }
  matchedApis {
    coveragePercent
    matchRole
    notes
    api { uuid name version }
  }
`;

export const GET_API_INTAKES = `
  query GetApiIntakes {
    apiIntakes {
      ${API_INTAKE_FIELDS}
    }
  }
`;

export const GET_API_INTAKE = `
  query GetApiIntake($uuid: ID!) {
    apiIntake(uuid: $uuid) {
      ${API_INTAKE_FIELDS}
    }
  }
`;

const PMG_FIELDS = `
  uuid
  tenantId
  name
  description
  owners
  members
  createdBy
  createdOn
  updatedOn
  assignedAssetCount
`;

export const GET_PRODUCT_MANAGEMENT_GROUPS = `
  query {
    productManagementGroups {
      ${PMG_FIELDS}
    }
  }
`;

export const GET_PRODUCT_MANAGEMENT_GROUP = `
  query GetProductManagementGroup($uuid: ID!) {
    productManagementGroup(uuid: $uuid) {
      ${PMG_FIELDS}
    }
  }
`;

export const GET_ENTITY_PMG_ASSIGNMENTS = `
  query EntityPmgAssignments($entityUuid: ID!) {
    entityPmgAssignments(entityUuid: $entityUuid) {
      entityUuid
      entityType
      entityName
      pmgUuid
      pmgName
    }
  }
`;

export const GET_PMG_ASSIGNED_ASSETS = `
  query PmgAssignedAssets($pmgUuid: ID!) {
    pmgAssignedAssets(pmgUuid: $pmgUuid) {
      entityUuid
      entityType
      entityName
      pmgUuid
      pmgName
    }
  }
`;
